import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Bell, X, Wrench, LogIn, KeyRound, UserCog, UserPlus, Archive, RotateCcw, Upload, CalendarPlus, CalendarClock, Trash2, Save, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const ACTION_MAP = {
  "auth.builder_login": { label: "logged in", icon: LogIn },
  "auth.builder_activated": { label: "activated their account", icon: KeyRound },
  "player.created": { label: "added player", icon: UserPlus },
  "player.updated": { label: "edited player", icon: UserCog },
  "player.archived": { label: "archived player", icon: Archive },
  "player.restored": { label: "restored player", icon: RotateCcw },
  "roster.uploaded": { label: "uploaded roster file", icon: Upload },
  "game.created": { label: "added game", icon: CalendarPlus },
  "game.updated": { label: "edited game", icon: CalendarClock },
  "game.deleted": { label: "deleted game", icon: Trash2 },
  "schedule.uploaded": { label: "uploaded schedule", icon: Upload },
  "stats.saved": { label: "saved stats for", icon: Save },
  "season.switched": { label: "switched active season to", icon: Sparkles },
  "season.started": { label: "started new season", icon: Sparkles },
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export function useNotifications() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [lastLoginItem, setLastLoginItem] = useState(null);
  const [dismissedLoginId, setDismissedLoginId] = useState(null);

  const load = useCallback(async () => {
    if (!user || user.role !== "coach") return;
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
      const lastSeen = data.last_seen;
      // Newest unread builder login → drives the banner
      const newLogin = (data.items || []).find(
        (i) => i.action === "auth.builder_login" && (!lastSeen || i.timestamp > lastSeen)
      );
      setLastLoginItem(newLogin || null);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!user || user.role !== "coach") return;
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, [user, load]);

  const markRead = useCallback(async () => {
    try {
      await api.post("/notifications/mark-read");
      setUnread(0);
      // reload to get fresh last_seen
      load();
    } catch { /* ignore */ }
  }, [load]);

  const dismissBanner = (id) => setDismissedLoginId(id);

  return { items, unread, lastLoginItem, dismissedLoginId, dismissBanner, markRead, refresh: load };
}

export function NotificationBanner({ item, onDismiss }) {
  if (!item) return null;
  return (
    <div
      className="bg-burgundy/20 border-b border-burgundy/40 px-4 md:px-8 py-2.5 flex items-center gap-3 text-sm animate-fade-up"
      data-testid="builder-login-banner"
    >
      <Wrench className="w-4 h-4 text-burgundy shrink-0" />
      <span className="flex-1 min-w-0 truncate">
        <span className="font-bold text-white">{item.actor_name}</span>
        <span className="text-ink-300"> logged in · {timeAgo(item.timestamp)}</span>
      </span>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-ink-300 hover:text-white transition"
        data-testid="dismiss-banner-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function NotificationBell() {
  const { user } = useAuth();
  const { items, unread, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (!open) return;
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user || user.role !== "coach") return null;

  const handleOpen = () => {
    if (!open && unread > 0) markRead();
    setOpen(!open);
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={handleOpen}
        aria-label="Notifications"
        className="relative p-2 rounded-md text-ink-300 hover:text-white hover:bg-ink-800 transition"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" strokeWidth={2.2} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-burgundy text-white text-[10px] font-bold ring-2 ring-ink-900"
            data-testid="notification-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute right-0 mt-2 w-80 md:w-96 bg-ink-900 border border-ink-700 rounded-lg shadow-2xl z-50 max-h-[70vh] overflow-hidden flex flex-col"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-ink-700/60">
            <p className="eyebrow flex items-center gap-2"><Wrench className="w-3.5 h-3.5" /> Builder Activity</p>
            {items.length > 0 && (
              <button onClick={markRead} className="text-[10px] uppercase tracking-wider text-burgundy hover:text-burgundy-hover" data-testid="mark-read-btn">
                Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-center text-ink-400 text-sm px-6 py-8">No builder activity yet.</p>
            ) : (
              items.map((it) => {
                const meta = ACTION_MAP[it.action] || { label: it.action, icon: Wrench };
                const Icon = meta.icon;
                return (
                  <div key={it.id} className={cn("flex items-start gap-3 px-4 py-3 border-b border-ink-700/30 last:border-0 hover:bg-ink-800/40 transition")}
                       data-testid={`notification-item-${it.action}`}>
                    <div className="w-8 h-8 rounded-md bg-burgundy/15 border border-burgundy/40 flex items-center justify-center text-burgundy shrink-0">
                      <Icon className="w-4 h-4" strokeWidth={2.2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm leading-snug">
                        <span className="font-bold text-white">{it.actor_name}</span>{" "}
                        <span className="text-ink-300">{meta.label}</span>
                        {it.target_label && (
                          <>{" "}<span className="text-white font-semibold">{it.target_label}</span></>
                        )}
                      </p>
                      <p className="text-[10px] uppercase tracking-wider text-ink-400 mt-1">{timeAgo(it.timestamp)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
